import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtL, fmtTime, ROLES } from "@/lib/api";
import { PageHeader, PlayerAvatar, Spinner, useConfirm } from "@/components/opl/Common";
import { Field, GoldBtn, GhostBtn, useList } from "@/components/opl/Crud";

const STAT_KEYS = ["matches", "runs", "wickets", "strike_rate", "economy", "best"];

export default function PlayerForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [cats] = useList("/categories");
  const [clubs] = useList("/clubs");
  const [v, setV] = useState(id ? null : { role: "Batsman", availability: "available", registration_status: "pending", stats: {}, tags: [] });
  const [busy, setBusy] = useState(false);
  const [dialog, confirm] = useConfirm();
  useEffect(() => { if (id) api.get(`/players/${id}`).then((r) => setV(r.data)); }, [id]);
  if (!v || !cats) return <Spinner />;
  const set = (k) => (x) => setV((s) => ({ ...s, [k]: x }));
  const cat = cats.find((c) => c.id === v.category_id);
  const F = (f) => <Field key={f.name} f={f} value={v[f.name]} onChange={set(f.name)} />;
  const submit = async (force = false) => {
    if (!v.name || !v.category_id || !v.role) return toast.error("Name, category and role are required");
    setBusy(true);
    const body = { ...v, force, tags: typeof v.tags === "string" ? v.tags.split(",").map((t) => t.trim()).filter(Boolean) : v.tags };
    try {
      if (id) await api.put(`/players/${id}`, body); else await api.post("/players", body);
      toast.success("Player saved");
      nav("/admin/players");
    } catch (e) {
      if (e.response?.status === 409) confirm({ title: "Possible duplicate", description: errMsg(e), confirmLabel: "Save anyway", onConfirm: () => submit(true), testid: "duplicate-dialog" });
      else toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      {dialog}
      <PageHeader title={id ? `Edit ${v.name}` : "Add Player"} crumbs={["Players", id ? "Edit" : "Add"]}>
        <GhostBtn testid="cancel-player-btn" onClick={() => nav("/admin/players")}>Cancel</GhostBtn>
        <GoldBtn testid="save-player-btn" disabled={busy} onClick={() => submit(false)}>Save player</GoldBtn>
      </PageHeader>
      <div className="grid xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <section className="surface rounded-2xl p-6 grid sm:grid-cols-2 gap-4" data-testid="player-form">
            <p className="eyebrow sm:col-span-2">Identity</p>
            {F({ name: "name", label: "Full name", required: true })}
            {F({ name: "photo", label: "Profile photograph", type: "image", folder: "players" })}
            {F({ name: "dob", label: "Date of birth", type: "date" })}
            {F({ name: "club_id", label: "Registered club", type: "select", options: (clubs || []).map((c) => ({ value: c.id, label: c.name })) })}
            {F({ name: "phone", label: "Phone (admin only)" })}
            {F({ name: "email", label: "Email (admin only)", type: "email" })}
          </section>
          <section className="surface rounded-2xl p-6 grid sm:grid-cols-2 gap-4">
            <p className="eyebrow sm:col-span-2">Cricket profile</p>
            {F({ name: "category_id", label: "Category", type: "select", required: true, options: cats.filter((c) => c.enabled || c.id === v.category_id).map((c) => ({ value: c.id, label: `${c.name} (base ${fmtL(c.base_price)})` })) })}
            {F({ name: "role", label: "Playing role", type: "select", required: true, options: ROLES.map((r) => ({ value: r, label: r })) })}
            {F({ name: "batting_style", label: "Batting style", placeholder: "Right-hand bat" })}
            {F({ name: "bowling_style", label: "Bowling style", placeholder: "Right-arm medium" })}
            {F({ name: "is_wicketkeeper", label: "Wicketkeeper", type: "switch" })}
            {F({ name: "experience", label: "Playing experience" })}
            <div className="sm:col-span-2 grid grid-cols-3 sm:grid-cols-6 gap-2">
              {STAT_KEYS.map((k) => <Field key={k} f={{ name: `stat_${k}`, label: k.replace("_", " ") }} value={v.stats?.[k] ?? ""} onChange={(x) => setV((s) => ({ ...s, stats: { ...s.stats, [k]: x } }))} />)}
            </div>
          </section>
          <section className="surface rounded-2xl p-6 grid sm:grid-cols-2 gap-4">
            <p className="eyebrow sm:col-span-2">Auction</p>
            {F({ name: "base_price", label: "Base price override (points)", type: "number", hint: `Leave empty to use category base (${fmtL(cat?.base_price)})` })}
            {F({ name: "availability", label: "Availability", type: "select", options: [{ value: "available", label: "Available" }, { value: "unavailable", label: "Unavailable" }] })}
            {F({ name: "registration_status", label: "Registration status", type: "select", options: ["pending", "approved", "rejected"].map((x) => ({ value: x, label: x })) })}
            <Field f={{ name: "tags", label: "Tags (comma separated)" }} value={Array.isArray(v.tags) ? v.tags.join(", ") : v.tags} onChange={set("tags")} />
            {F({ name: "notes", label: "Notes", type: "textarea", full: true })}
          </section>
        </div>
        <div className="space-y-4">
          <p className="eyebrow">Auction card preview</p>
          <div className="surface-2 rounded-2xl p-6 text-center" data-testid="player-card-preview">
            <PlayerAvatar player={v} size={140} className="mx-auto" />
            <h3 className="text-2xl font-black mt-4">{v.name || "Player name"}</h3>
            <p className="text-sm text-[#FF8A65] mt-1">{v.role} · <span className="text-[#F5D061]">{cat?.name || "Category"}</span></p>
            <p className="eyebrow mt-4">Base price</p>
            <p className="num text-3xl font-black gold-text">{fmtL(v.base_price ?? cat?.base_price)}</p>
          </div>
          {id && v.bids?.length > 0 && (
            <div className="surface rounded-xl p-4"><p className="eyebrow mb-2">Bid history</p>
              {v.bids.map((b) => <div key={b.id} className="flex justify-between text-xs py-1"><span>{b.team_id?.slice(0, 6)} · {b.status}</span><span className="num">{fmtL(b.amount)}</span><span className="text-slate-500">{fmtTime(b.created_at)}</span></div>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
